// src/com/com_info/ComInfoInput.js
import React, { useEffect, useState } from 'react';
import { Box } from '@mui/system';
// import { styled } from '@mui/material/styles';
import {
    Button, DialogContent, DialogActions, Grid, CircularProgress, Popover
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { AppWord, AppObject, AppStyle } from '../../value/AppConst';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import DraggableEditor from '../../common/DraggableEditor';
import VzmTextField from '../../common/VzmTextField';
import { ErrorHintBox } from '../../common/VzmStyled';
import InputUtil from '../../value/InputUtil';
import { ComInfoDictionary } from './ComInfoDict';
import IsValid from '../../value/IsValid';
import DaumAddressSearch from '../../common/DaumAddressSearch';

const InputMap = ComInfoDictionary.inputMap;
const defaultInputData = InputUtil.defaultValuesFromInputMap(InputMap);

/**
 * (COM_INFO) 정보 생성 또는 편집 Dialog
 * Company Info
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2026-06-30 16:13:49 using DbdbTap
 * https://www.dbdbtap.com
 */
export default function ComInfoInput({
    open,
    item, // always. no new.
    onClose,
    onRequestSave,
    responseSaving, // Response if result is NOT OK.
}) {
    const [input, setInput] = useState({...AppObject.emptyInputData});
    // const codeTree = useReactiveVar(codeTreeRepo);
    const [anchorAddr, setAnchorAddr] = useState(null);
    const openConfirmDialog = useConfirmDialog();
    // const addAlert = useAlertSetter();
    const {loading:loadingSave, error:errorSave, data:resultSave} = responseSaving; // 전송 결과에 대한 View 기능 소화.

    // const dcTypeCdOptions = ValueMisc.makeCodesToSelectList(codeTree, CodeGroup.DC_TYPE_CD, InputMap.dcTypeCd.id);

    useEffect(()=>{
        if(open) {
            const inputSet = {...AppObject.emptyInputData};
            if(item) {
                inputSet.data = {...item};
            }
            else {
                inputSet.data = {...defaultInputData};
            }
            setInput(inputSet);
        }
    }, [open, item]);

    const onChangeValue = (id, value, error) => {
        const newInput = InputUtil.reflectData(input, id, value, error, InputMap, true);
        setInput(newInput);
    };
    
    const onSelectAddress = (addr) => {
        if(addr) {
            const newInputOne = InputUtil.reflectData(input, InputMap.zipCd.id, addr.zipCd, !IsValid.zipCode(addr.zipCd), InputMap, true);
            const newInput = InputUtil.reflectData(newInputOne, InputMap.addr.id, addr.addr, !IsValid.string(addr.addr, 0, 100), InputMap, true);
            setInput(newInput);
        }
        setAnchorAddr(null);
    };

    const onClickSubmit = () => {
        const param = InputUtil.getDataToSubmit(input.data, InputMap, true);
        onRequestSave(param, true);
    };

    const askCancel = async () => {
        const confirm = await openConfirmDialog({
            ...AppObject.messageCancelEdit,
            option: {yesLabel: AppWord.IgnoreAndClose}
        });
        if(confirm) onClose();
    };

    const onClickCancel = () => {
        if(input.changed) askCancel();
        else onClose();
    };

    if(!item || !open) return null; // item is always. no new.

    // Input field initiallized using getInputField(c,t,i)
    return(
        <DraggableEditor open={open} maxWidth='md'
            onClose={onClickCancel}
            title={'출발지 운송사 편집: ' + item.comName}
            startIcon={<LocalShippingIcon size={AppWord.LARGE}/>}
        >
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <VzmTextField
                            dict={InputMap.comId}
                            value={input.data.comId}
                            onChange={onChangeValue}
                            fullWidth={true}
                            disabled={true}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <VzmTextField
                            dict={InputMap.comName}
                            value={input.data.comName}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <VzmTextField
                            dict={InputMap.billJobType}
                            value={input.data.billJobType}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <VzmTextField
                            dict={InputMap.billJobClass}
                            value={input.data.billJobClass}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <VzmTextField
                            dict={InputMap.comAlias}
                            value={input.data.comAlias}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <VzmTextField
                            dict={InputMap.reprName}
                            value={input.data.reprName}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <VzmTextField
                            dict={InputMap.officeTel}
                            value={input.data.officeTel}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <VzmTextField
                            dict={InputMap.faxTel}
                            value={input.data.faxTel}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <VzmTextField
                            dict={InputMap.zipCd}
                            value={input.data.zipCd}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={6}>
                            <Button
                                style={{marginLeft:8}} size={AppWord.LARGE}
                                variant={AppStyle.mui.contained}
                                color={AppStyle.mui.primary}
                                onClick={(e)=>{setAnchorAddr(e.currentTarget);}}
                            >
                                주소찾기
                            </Button>
                            <Popover
                                anchorEl={anchorAddr}
                                open={Boolean(anchorAddr)}
                                onClose={()=>{setAnchorAddr(null);}}
                                anchorOrigin={{ vertical: 'top', horizontal: 'right', }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right', }}
                            >
                                <DaumAddressSearch open={Boolean(anchorAddr)} onSelect={onSelectAddress}/>
                            </Popover>
                    </Grid>
                    <Grid item xs={12}>
                        <VzmTextField
                            dict={InputMap.addr}
                            value={input.data.addr}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Box
                            style={{
                                fontSize: '0.9rem', color: AppStyle.PrimaryRGB,
                                padding: '8px 0px 4px 0px'
                            }}
                        >
                            담당자 정보
                        </Box>
                    </Grid>
                    <Grid item xs={6}>
                        <VzmTextField
                            dict={InputMap.agentName}
                            value={input.data.agentName}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <VzmTextField
                            dict={InputMap.agentTelNo}
                            value={input.data.agentTelNo}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <VzmTextField
                            dict={InputMap.agentEmail}
                            value={input.data.agentEmail}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                
                </Grid>
            </DialogContent>
            <DialogActions style={{display:'flex'}}>
                {
                    input.errorLabels && input.errorLabels.length > 0
                    ?
                    <ErrorHintBox>
                        확인: {input.errorLabels.join(', ')}
                    </ErrorHintBox>
                    : null
                }
                {
                    loadingSave
                    ?
                    <span>
                        <CircularProgress />
                        &nbsp;데이터 저장 중...
                    </span>
                    :
                    <>
                        <Button
                            onClick={onClickSubmit}
                            disabled={input.hasError || !input.changed}
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.primary}
                        >저장</Button>
                        <Button onClick={onClickCancel}
                            color={AppStyle.mui.warning}
                            variant={AppStyle.mui.contained}
                        >취소</Button>
                    </>
                }
            </DialogActions>
        </DraggableEditor>
    );
}
